import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { httpClient } from "@bw/shared/api";

import {
  cancelTournament,
  getPublishReadiness,
  publishTournament,
  type Tournament,
  type TournamentPublishReadiness,
} from "@/libs/tournamentApi";

type PendingAction =
  | "publish"
  | "close-registration"
  | "seed-draws"
  | "start"
  | "cancel";

function errorMessage(cause: unknown, fallback: string) {
  return cause instanceof Error ? cause.message : fallback;
}

export function StatusActions({
  tournament,
  onUpdated,
  hidePublish = false,
}: {
  tournament: Tournament;
  onUpdated: () => void | Promise<void>;
  /** #2691: the tournament page opens registration from its header (which
   * places the court hold); the old Publish button would bypass that copy. */
  hidePublish?: boolean;
}) {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const sessionGenerationRef = useRef(0);
  const readinessGenerationRef = useRef(0);
  const readinessInFlightRef = useRef(false);
  const pendingActionRef = useRef<PendingAction | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const [publishOpen, setPublishOpen] = useState(false);
  const [loadingReadiness, setLoadingReadiness] = useState(false);
  const [readiness, setReadiness] = useState<TournamentPublishReadiness | null>(
    null,
  );
  const [publishError, setPublishError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      readinessGenerationRef.current += 1;
      readinessInFlightRef.current = false;
      pendingActionRef.current = null;
    };
  }, []);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    readinessGenerationRef.current += 1;
    readinessInFlightRef.current = false;
    pendingActionRef.current = null;
    setPendingAction(null);
    setPublishOpen(false);
    setLoadingReadiness(false);
    setReadiness(null);
    setPublishError(null);
    setCancelOpen(false);
    setError(null);
  }, [tournament.tournament_id]);

  const lifecycleStatus = tournament.effective_status ?? tournament.status;
  const readinessBlockers = readiness?.blockers ?? [];
  const readinessWarnings = readiness?.warnings ?? [];
  const readinessChecks = readiness?.checks ?? [];
  const canPublish = !hidePublish && !tournament.is_published;
  const canCloseRegistration = lifecycleStatus === "registration_open";
  const canSeedDraws = lifecycleStatus === "registration_closed";
  const canStart = lifecycleStatus === "registration_closed";
  const canReviewCloseout = lifecycleStatus === "in_progress";
  const canCancel =
    tournament.is_published &&
    lifecycleStatus !== "archived" &&
    lifecycleStatus !== "completed";

  const isCurrentSession = (sessionGeneration: number) =>
    mountedRef.current && sessionGenerationRef.current === sessionGeneration;

  const loadPublishReadiness = async () => {
    if (readinessInFlightRef.current) return;

    const sessionGeneration = sessionGenerationRef.current;
    const readinessGeneration = readinessGenerationRef.current + 1;
    readinessGenerationRef.current = readinessGeneration;
    readinessInFlightRef.current = true;
    setError(null);
    setPublishError(null);
    setReadiness(null);
    setLoadingReadiness(true);

    try {
      const nextReadiness = await getPublishReadiness(tournament.tournament_id);
      if (
        isCurrentSession(sessionGeneration) &&
        readinessGenerationRef.current === readinessGeneration
      ) {
        setReadiness(nextReadiness);
      }
    } catch (cause) {
      if (
        isCurrentSession(sessionGeneration) &&
        readinessGenerationRef.current === readinessGeneration
      ) {
        setPublishError(
          errorMessage(cause, "Failed to check publish readiness"),
        );
      }
    } finally {
      if (readinessGenerationRef.current === readinessGeneration) {
        readinessInFlightRef.current = false;
      }
      if (
        isCurrentSession(sessionGeneration) &&
        readinessGenerationRef.current === readinessGeneration
      ) {
        setLoadingReadiness(false);
      }
    }
  };

  const handlePublishClick = () => {
    setPublishOpen(true);
    void loadPublishReadiness();
  };

  const requestPublishClose = () => {
    if (pendingActionRef.current === "publish") return;
    readinessGenerationRef.current += 1;
    readinessInFlightRef.current = false;
    setPublishOpen(false);
    setLoadingReadiness(false);
    setReadiness(null);
    setPublishError(null);
  };

  const beginAction = (action: PendingAction) => {
    if (pendingActionRef.current !== null) return null;
    const sessionGeneration = sessionGenerationRef.current;
    pendingActionRef.current = action;
    setPendingAction(action);
    return sessionGeneration;
  };

  const finishAction = (action: PendingAction, sessionGeneration: number) => {
    if (pendingActionRef.current === action) {
      pendingActionRef.current = null;
    }
    if (isCurrentSession(sessionGeneration)) {
      setPendingAction(null);
    }
  };

  const handlePublish = async () => {
    const sessionGeneration = beginAction("publish");
    if (sessionGeneration === null) return;

    setPublishError(null);
    try {
      await publishTournament(tournament.tournament_id);
      if (!isCurrentSession(sessionGeneration)) return;
      setPublishOpen(false);
      await onUpdated();
    } catch (cause) {
      if (isCurrentSession(sessionGeneration)) {
        setPublishError(errorMessage(cause, "Failed to publish tournament"));
      }
    } finally {
      finishAction("publish", sessionGeneration);
    }
  };

  const runLifecycleAction = async (
    action: PendingAction,
    endpoint: string,
    fallback: string,
  ) => {
    const sessionGeneration = beginAction(action);
    if (sessionGeneration === null) return;

    setError(null);
    try {
      await httpClient.post<Tournament>(
        "/tournaments/" + tournament.tournament_id + "/" + endpoint,
      );
      if (isCurrentSession(sessionGeneration)) await onUpdated();
    } catch (cause) {
      if (isCurrentSession(sessionGeneration)) {
        setError(errorMessage(cause, fallback));
      }
    } finally {
      finishAction(action, sessionGeneration);
    }
  };

  const handleCancel = async () => {
    const sessionGeneration = beginAction("cancel");
    if (sessionGeneration === null) return;

    setError(null);
    try {
      await cancelTournament(tournament.tournament_id);
      if (isCurrentSession(sessionGeneration)) navigate("/tournaments");
    } catch (cause) {
      if (isCurrentSession(sessionGeneration)) {
        setError(errorMessage(cause, "Failed to cancel tournament"));
      }
    } finally {
      finishAction("cancel", sessionGeneration);
    }
  };

  if (
    !canPublish &&
    !canCloseRegistration &&
    !canSeedDraws &&
    !canStart &&
    !canReviewCloseout &&
    !canCancel
  ) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {canPublish ? (
          <Button
            disabled={pendingAction !== null || loadingReadiness}
            onClick={handlePublishClick}
            size="small"
            variant="contained"
          >
            {loadingReadiness ? "Checking…" : "Publish"}
          </Button>
        ) : null}
        {canCloseRegistration ? (
          <Button
            disabled={pendingAction !== null}
            onClick={() =>
              void runLifecycleAction(
                "close-registration",
                "close-registration",
                "Failed to close registration",
              )
            }
            size="small"
            variant="contained"
          >
            {pendingAction === "close-registration"
              ? "Closing…"
              : "Close registration"}
          </Button>
        ) : null}
        {canSeedDraws ? (
          <Button
            disabled={pendingAction !== null}
            onClick={() =>
              void runLifecycleAction(
                "seed-draws",
                "seed-draws",
                "Failed to seed draws",
              )
            }
            size="small"
            variant="outlined"
          >
            {pendingAction === "seed-draws" ? "Seeding…" : "Seed draws"}
          </Button>
        ) : null}
        {canStart ? (
          <Button
            disabled={pendingAction !== null}
            onClick={() =>
              void runLifecycleAction(
                "start",
                "start",
                "Failed to start tournament",
              )
            }
            size="small"
            variant="contained"
          >
            {pendingAction === "start" ? "Starting…" : "Start tournament"}
          </Button>
        ) : null}
        {canReviewCloseout ? (
          <Button
            onClick={() => navigate("?tab=closeout")}
            size="small"
            variant="contained"
          >
            Review closeout
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            color="error"
            disabled={pendingAction !== null}
            onClick={() => setCancelOpen(true)}
            size="small"
            variant="outlined"
          >
            Cancel tournament
          </Button>
        ) : null}
      </Stack>

      {error && !cancelOpen ? <Alert severity="error">{error}</Alert> : null}

      <Dialog
        aria-describedby="publish-tournament-description"
        aria-labelledby="publish-tournament-title"
        fullWidth
        maxWidth="sm"
        onClose={requestPublishClose}
        open={publishOpen}
      >
        <DialogTitle id="publish-tournament-title">
          Publish tournament?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText id="publish-tournament-description">
              {readiness?.is_ready
                ? tournament.name + " is ready to publish."
                : tournament.name + " needs these checks before publishing."}
            </DialogContentText>

            {loadingReadiness ? (
              <Stack
                direction="row"
                role="status"
                spacing={1}
                sx={{ alignItems: "center" }}
              >
                <CircularProgress size={18} />
                <Typography color="text.secondary" variant="body2">
                  Checking readiness…
                </Typography>
              </Stack>
            ) : null}

            {publishError ? (
              <Alert severity="error">{publishError}</Alert>
            ) : null}

            {readiness ? (
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  Events configured: {readiness.event_count}
                </Typography>
                {readinessBlockers.length > 0 ? (
                  <Alert severity="error">
                    <Stack spacing={0.5}>
                      {readinessBlockers.map((blocker) => (
                        <Typography key={blocker} variant="body2">
                          {blocker}
                        </Typography>
                      ))}
                    </Stack>
                  </Alert>
                ) : null}
                {readinessWarnings.length > 0 ? (
                  <Alert severity="warning">
                    <Stack spacing={0.5}>
                      {readinessWarnings.map((warning) => (
                        <Typography key={warning} variant="body2">
                          {warning}
                        </Typography>
                      ))}
                    </Stack>
                  </Alert>
                ) : null}
                <Stack spacing={1}>
                  {readinessChecks.map((check) => (
                    <Paper key={check.key} variant="outlined">
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          p: 1.5,
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 600 }} variant="body2">
                            {check.label}
                          </Typography>
                          {!check.is_complete && check.message ? (
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              {check.message}
                            </Typography>
                          ) : null}
                        </Box>
                        <Chip
                          color={
                            check.is_complete
                              ? "success"
                              : check.required
                                ? "error"
                                : "default"
                          }
                          label={
                            check.is_complete
                              ? "Done"
                              : check.required
                                ? "Required"
                                : "Optional"
                          }
                          size="small"
                          variant={check.is_complete ? "filled" : "outlined"}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          {publishError && !loadingReadiness ? (
            <Button
              disabled={pendingAction !== null}
              onClick={() => void loadPublishReadiness()}
              size="small"
            >
              Retry
            </Button>
          ) : null}
          <Button
            disabled={pendingAction === "publish"}
            onClick={requestPublishClose}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={
              pendingAction !== null || loadingReadiness || !readiness?.is_ready
            }
            onClick={() => void handlePublish()}
            size="small"
            variant="contained"
          >
            {pendingAction === "publish" ? "Publishing…" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-describedby="cancel-tournament-description"
        aria-labelledby="cancel-tournament-title"
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (pendingActionRef.current !== "cancel") setCancelOpen(false);
        }}
        open={cancelOpen}
        slotProps={{ paper: { role: "alertdialog" } }}
      >
        <DialogTitle id="cancel-tournament-title">
          Cancel tournament?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText id="cancel-tournament-description">
              This cancels {tournament.name} and returns to the tournament list.
            </DialogContentText>
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={pendingAction === "cancel"}
            onClick={() => setCancelOpen(false)}
            size="small"
            variant="outlined"
          >
            Keep tournament
          </Button>
          <Button
            color="error"
            disabled={pendingAction !== null}
            onClick={() => void handleCancel()}
            size="small"
            variant="contained"
          >
            {pendingAction === "cancel" ? "Cancelling…" : "Cancel tournament"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
